import { useState, useEffect, useContext, useRef } from "react";
import { useParams, useNavigate } from "react-router";
import { loadOrgModel } from "../../api/connection";
import { createDeployment, Deployment } from "../../models/Deployment";
import { createRobot, Robot } from "../../models/Robot";
import { SiteAnchor, createSiteAnchor } from "../../models/SiteAnchor";
import { getCreds, PageContext } from "../../contexts/PageContext";
import { LiveCamera } from "./LiveCamera";
import { RobotAgent } from "../../panels/worldpanelparts/robot/RobotAgent";
import DeploymentWorldPanel from "./DeploymentWorldPanel";
import { RobotVisual } from "../../visuals/RobotVisual";
import { BaseVisual } from "../../visuals/BaseVisual";
import { RCPanel } from "../../panels/rc/RCPanel";
import { SecondaryToolbar, TOOLBAR_HEIGHT } from "./SecondaryToolbar";
import { Typography } from "@mui/material";
import { CameraToolbar } from "./CameraToolbar";
import { FlexContainer } from "../../components/FlexContainer";
import { UniversalToolbar } from "./UniversalToolbar";
import { MapToolbar } from "./MapToolbar";
import { Vec2 } from "../../models/SyntheticMap";
import { RobotSettingsDialog } from "./RobotSettingsDialog";
import { runningOnRobot } from "../../api/host-aware";
import { setAlternateSignalingUrl } from "../../api/robocore/wrtc_connection";

export interface DeploymentContext {
  deployment: Deployment;
  site: SiteAnchor;
  robot: Robot;
  agent?: RobotAgent;
  settingsOpen?: boolean;
}

export enum SecondaryWindowSize {
  Min = 0,
  Norm = 1,
  Max = 2,
}


export function DeploymentPage() {
  let { id } = useParams<{ id: string }>();
  const refMap = useRef<HTMLDivElement>(null);
  const refVideo = useRef<HTMLDivElement>(null);
  const [ctx, setCtx] = useState<DeploymentContext>();
  const [videoPrimary, setVideoPrimary] = useState(false);
  const [secMode, setSecMode] = useState(SecondaryWindowSize.Norm);
  const [mapSize, setMapSize] = useState<Vec2>({ x: 0, y: 0 });
  if (!id && runningOnRobot()) {
    id = "default";
  }
  useEffect(() => {
    const { user } = getCreds();
    if (window.location.href.includes('?signaling=')) {
      const signalingParam = new URLSearchParams(window.location.search).get('signaling');
      setAlternateSignalingUrl(`ws://${signalingParam}:28801`);
    }
    const fetchDeployment = async () => {
      const deployment = await loadOrgModel(createDeployment(id));
      const robot = await loadOrgModel(createRobot(deployment.robots[0].robotId));
      const site = await loadOrgModel(createSiteAnchor(deployment.siteId));
      site.showBox = true;
      setCtx({
        robot,
        deployment,
        site
      });
    };
    if (user) {
      fetchDeployment().catch(console.error);
    }
    // debugger;
  }, [id]);

  const primaryStyle = {
    position: 'relative',
    zIndex: 1,
    padding: 0,
    margin: 0,
    height: '100vh',
    width: '100%'
  };
  const secWindowSizes = [
    {
      width: 64,
      height: 0,
      margin: 20
    },
    {
      width: 640,
      height: 360,
      margin: 20
    },
    {
      width: 1080,
      height: 720,
      margin: 20,
    }
  ];
  const secWindowSize = secWindowSizes[secMode];
  const secondaryStyle = {
    background: 'red',
    zIndex: 800,
    position: 'absolute',
    top: `calc(100vh - ${secWindowSize.height}px - ${secWindowSize.margin}px)`,
    right: `${secWindowSize.margin}px`,
    padding: 0,
    margin: 0,
    width: `${secWindowSize.width}px`,
    height: `${secWindowSize.height}px`
  };
  const rcPanelHeight = 119;
  const rcPanelStyle = {
    position: 'absolute',
    top: `calc(100vh - ${rcPanelHeight}px - ${secWindowSize.margin}px)`,
    right: 2 * secWindowSize.margin + secWindowSize.width,
    width: 490,
    height: rcPanelHeight,
    background: 'transparent',
    zIndex: 900
  };

  const secondaryToolbarStyle = {
    position: 'absolute',
    top: `calc(100vh - ${secWindowSize.height}px - ${secWindowSize.margin}px - ${TOOLBAR_HEIGHT}px)`,
    right: `${secWindowSize.margin}px`,
    width: `${secWindowSize.width}px`,
    height: `${TOOLBAR_HEIGHT}px`,
    zIndex: 900
  };

  function switcher(increment: number) {
    if (secMode === SecondaryWindowSize.Max && increment > 0) {
      setSecMode(SecondaryWindowSize.Norm);
      const normSecSize = secWindowSizes[SecondaryWindowSize.Norm];
      if (videoPrimary) {
        switchPrimary('vox');
        setMapSize({ x: refMap.current?.clientWidth || 0, y: refMap.current?.clientHeight || 0 });
      } else {
        switchPrimary('video');
        setMapSize({ x: normSecSize.width, y: normSecSize.height });
      }
    } else {
      setSecMode(secMode + increment);
      const normSecSize = secWindowSizes[secMode + increment];
      if (videoPrimary) {
        setMapSize({ x: normSecSize.width, y: normSecSize.height });
      }
    }
  }

  // console.log({ videoPrimary });
  function switchPrimary(primary: 'video' | 'vox') {
    // console.log({ primary, videoPrimary });
    if (primary === 'video' && !videoPrimary) {
      setVideoPrimary(true);
    } else if (primary === 'vox' && videoPrimary) {
      setVideoPrimary(false);
    }
  }
  
  if (!ctx) {
    return <Typography variant="h5">Authenticating and loading</Typography>
  }

  return (
    <div>
      <div style={(videoPrimary ? secondaryStyle : primaryStyle) as any} ref={refMap}>
        <DeploymentWorldPanel
          activeSiteId={ctx.site._id}
          rendererKey={Date.now().toString()}
          size={mapSize}
          onLoaded={async (wr) => {
            if (ctx.agent) {
              return;
            }
            Array.from(wr.dataService.visuals.values()).forEach((v: BaseVisual) => {
              if (v instanceof RobotVisual && v.robotId === ctx.robot._id) {
                const agent = new RobotAgent(v, true);
                setCtx({ ...ctx, agent });
              }
            });
          }}
        />
      </div>
      <div style={(videoPrimary ? primaryStyle : secondaryStyle) as any} ref={refVideo}>
        {ctx.agent &&
          <LiveCamera agent={ctx.agent} />
        }
      </div>
      <div style={{
        position: 'absolute',
        top: '20px',
        left: '80px',
        right: '20px',
        height: TOOLBAR_HEIGHT,
        zIndex: 1900
      }}>
        {ctx.agent && <FlexContainer sx={{ width: '100%' }}>
          <div style={{ display: 'inline-block' }}>
            {videoPrimary ? <CameraToolbar agent={ctx.agent!} /> : <MapToolbar agent={ctx.agent!} />}
          </div>
          <UniversalToolbar ctx={ctx} setCtx={setCtx} />
        </FlexContainer>
        }
      </div>
      <div style={secondaryToolbarStyle as any}>
        <SecondaryToolbar switcher={switcher} secMode={secMode}>
          {(ctx.agent && videoPrimary) && <MapToolbar agent={ctx.agent!} />}
          {(ctx.agent && !videoPrimary) && <CameraToolbar agent={ctx.agent!} />}
        </SecondaryToolbar>
      </div>
      <div style={rcPanelStyle as any}>
        {ctx.agent && <RCPanel robot={ctx.agent} />}
      </div>
      <RobotSettingsDialog ctx={ctx} setCtx={setCtx} />
    </div>
  );
}